import React, { useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Button, Icon } from 'foundations-library/components'
import { getVerbalColors } from 'helpers'
import EasyStep from '../EasyStep'
import { getValue } from 'remote-config-value'
import { Dialog } from 'functional-foundations'

const useStyles = makeStyles((theme: any) => {
    const { colors } = theme['foundations-library'] || {}
    const verbalColors = getVerbalColors(colors)

    return {
        root: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '66px',
            width: '100%'
        },

        header: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
        },

        subtitleGap10: {
            marginTop: '10px'
        },

        subtitleText: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: '23px',
            letterSpacing: '-0.2px',
            textAlign: 'center'
        },

        infoTextWrap: {
            marginTop: '10px',
            textAlign: 'center'
        },

        learnMoreLink: {
            color: verbalColors['primary-600'],
            textDecoration: 'underline'
        },

        watchDemoWrap: {
            marginTop: '15px',
            display: 'flex',
            justifyContent: 'center'
        },

        stepperWrap: {
            marginTop: '40px',
            width: '780px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 34px',
            boxSizing: 'border-box'
        },

        stepperItem: {
            display: 'flex',
            alignItems: 'center',
            flex: 1,

            '&:last-child $stepperLine': {
                display: 'none'
            }
        },

        stepCircle: {
            width: '44px',
            height: '44px',
            borderRadius: '22px',
            border: `2px solid ${verbalColors['primary-600']}`,
            backgroundColor: verbalColors['primary-500'],
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            flexShrink: 0
        },

        stepLine: {
            height: '1px',
            backgroundColor: verbalColors['neutral-50'],
            flex: 1,
            margin: '0 12px'
        },

        stepsRow: {
            display: 'flex',
            gap: '30px'
        },

        card: {
            marginTop: '20px',
            width: '930px'
        },

        footer: {
            marginTop: '30px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },

        btn: {
            width: '200px',
            height: '36px'
        },
        demoDialog: {
            '& .dialog-container': {
                padding: '0 !important'
            },
            '& .close-dialog-x': {
                display: 'none'
            }
        },
        videoModalContent: {
            position: 'relative',
            width: '1280px',
            height: '720px',
            backgroundColor: '#000000',
            overflow: 'hidden',
            '&:fullscreen': {
                width: '100vw',
                height: '100vh'
            },
            '& iframe': {
                display: 'block',
                width: '100%',
                height: '100%',
                border: 'none'
            }
        },
        videoControlButton: {
            position: 'absolute',
            top: '35px',
            zIndex: 2,
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '999px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(20, 20, 20, 0.72)',
            cursor: 'pointer'
        },
        fullscreenButton: {
            right: '72px'
        },
        closeButton: {
            right: '20px'
        }
    }
})

type PropsType = {
    onClickAddNew(): unknown
}

/**
 *
 */
const LandingPage = ({ onClickAddNew }: PropsType) => {
    const classes = useStyles()
    const [showDemoModal, setShowDemoModal] = useState(false)
    const videoModalRef = useRef<HTMLDivElement | null>(null)

    const closeDemoModal = async () => {
        if (document.fullscreenElement && typeof document.exitFullscreen === 'function') {
            await document.exitFullscreen()
        }

        setShowDemoModal(false)
    }

    const toggleVideoFullscreen = async () => {
        if (!videoModalRef.current || typeof videoModalRef.current.requestFullscreen !== 'function') {
            return
        }

        if (document.fullscreenElement === videoModalRef.current && typeof document.exitFullscreen === 'function') {
            await document.exitFullscreen()
            return
        }

        await videoModalRef.current.requestFullscreen()
    }

    const easySteps = [
        {
            title: getValue('sms_registration_step_1_title'),
            subtitle: getValue('sms_registration_step_1_subtitle'),
            badge: getValue('sms_registration_step_1_badge'),
            img: getValue('sms_registration_step_1_img')
        },
        {
            title: getValue('sms_registration_step_2_title'),
            subtitle: getValue('sms_registration_step_2_subtitle'),
            badge: getValue('sms_registration_step_2_badge'),
            img: getValue('sms_registration_step_2_img')
        },
        {
            title: getValue('sms_registration_step_3_title'),
            subtitle: getValue('sms_registration_step_3_subtitle'),
            badge: getValue('sms_registration_step_3_badge'),
            img: getValue('sms_registration_step_3_img')
        }
    ]

    return (
        <div className={classes.root} data-testid='landing-page'>
            <div className={classes.header}>
                <Typography variant='h4' color='neutral-900' data-testid='landing-title'>
                    {getValue('sms_registration_title')}
                </Typography>

                <div className={classes.subtitleGap10}>
                    <Typography color='neutral-900' className={classes.subtitleText} data-testid='landing-page-subtitle'>
                        {getValue('sms_registration_subtitle_1')}
                    </Typography>
                </div>

                <div className={classes.subtitleGap10}>
                    <Typography color='neutral-900' className={classes.subtitleText} data-testid='landing-page-subtitle-2'>
                        {getValue('sms_registration_subtitle_2')}
                    </Typography>
                </div>

                <div className={classes.infoTextWrap}>
                    <Typography color='neutral-900' className={classes.subtitleText} data-testid='landing-page-toll-free'>
                        This registration process does not apply to toll-free numbers.{' '}
                        <a
                            href={getValue('sms_registration_brands_learn_more_link')}
                            target="_blank"
                            className={classes.learnMoreLink} rel="noreferrer"
                            data-testid='landing-page-learn-more'
                        >
                            Learn more here.
                        </a>
                    </Typography>
                </div>

                <div className={classes.watchDemoWrap}>
                    <Button
                        variant='outlined'
                        color='neutral'
                        icon='play-outline'
                        iconLeft
                        className={classes.btn}
                        onClick={() => setShowDemoModal(true)}
                        data-testid='landing-page-btn-watch-demo'
                    >
                        {getValue('sms_registration_watch_demo_button')}
                    </Button>
                </div>
            </div>

            <div className={classes.stepperWrap}>
                {[1, 2, 3].map((step, idx) => (
                    <React.Fragment key={step}>
                        <div className={classes.stepCircle}>
                            <Typography color='white'>
                                {step}
                            </Typography>
                        </div>

                        {idx < 2 && <div className={classes.stepLine} />}
                    </React.Fragment>
                ))}
            </div>

            <div className={classes.card}>
                <div className={classes.stepsRow}>
                    {easySteps.map((step, idx) => (
                        <EasyStep
                            key={`sms-registration-step-${idx}`}
                            title={step.title}
                            subtitle={step.subtitle}
                            badge={step.badge}
                            img={step.img}
                            data-testid={`landing-page-easy-step-${idx}`}
                        />
                    ))}
                </div>

                <div />
            </div>

            <div className={classes.footer}>
                <Button
                    variant='contained'
                    color='primary'
                    icon='add'
                    iconLeft
                    className={classes.btn}
                    onClick={onClickAddNew}
                    data-testid='landing-page-start-btn'
                >
                    {getValue('sms_registration_start_button')}
                </Button>
            </div>
            <Dialog
                isOpen={showDemoModal}
                onClose={closeDemoModal}
                width={1280}
                className={classes.demoDialog}
                closeOnClickOutside
                data-testid='watch-demo-dialog'
            >
                {showDemoModal && (
                    <div className={classes.videoModalContent} ref={videoModalRef}>
                        <button
                            type='button'
                            className={`${classes.videoControlButton} ${classes.fullscreenButton}`}
                            onClick={toggleVideoFullscreen}
                            aria-label='Toggle fullscreen video'
                            data-testid='watch-demo-dialog-fullscreen-button'
                        >
                            <Icon name='open-in-new-alt' color='white' size={20}/>
                        </button>
                        <button
                            type='button'
                            className={`${classes.videoControlButton} ${classes.closeButton}`}
                            onClick={closeDemoModal}
                            aria-label='Close demo video dialog'
                            data-testid='watch-demo-dialog-video-close-button'
                        >
                            <Icon name='close' color='white' size={20}/>
                        </button>
                        <iframe
                            src='https://www.youtube.com/embed/Bp9HVG3xVLs?fs=0'
                            title='SMS Registration Demo'
                            frameBorder='0'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                            allowFullScreen
                        />
                    </div>
                )}
            </Dialog>
        </div>
    )
}

export default LandingPage
